import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { play } from '../audio/sfx'
import { CHARACTERS, PROFESSIONS } from '../game/characters'
import { professionExpenses } from '../game/finance'
import { inviteUrl } from '../net/client'
import { MAX_SEATS } from '../net/protocol'
import { useGame } from '../store/gameStore'
import { money } from '../util/format'

export function Lobby() {
  const { net, updateSeat, addBot, removeSeat, updateSettings, startOnline, leaveRoom, dismissNetError } =
    useGame()
  const [copied, setCopied] = useState(false)
  const room = net.room
  const seatCount = room?.seats.length ?? 0
  const prevSeats = useRef(seatCount)

  useEffect(() => {
    if (seatCount > prevSeats.current) play('join')
    prevSeats.current = seatCount
  }, [seatCount])

  if (!room) {
    return (
      <div className="setup-scroll">
        <div className="setup lobby-waiting">
          <div className="card setup-card lobby-status">
            <span className="spinner" aria-hidden />
            <h2>{net.status === 'reconnecting' ? 'กำลังต่อใหม่…' : 'กำลังเชื่อมต่อห้อง…'}</h2>
            {net.error && <p className="bad-text">{net.error}</p>}
            <button className="btn btn-ghost" onClick={leaveRoom}>
              ← กลับหน้าแรก
            </button>
          </div>
        </div>
      </div>
    )
  }

  const me = room.seats.find((s) => s.id === net.seatId)
  const isHost = net.seatId === room.hostSeat
  const link = inviteUrl(room.code)
  const humans = room.seats.filter((s) => !s.isBot).length
  const takenChars = new Set(room.seats.filter((s) => s.id !== net.seatId).map((s) => s.characterId))

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      /* บางเบราว์เซอร์ต้องกดจากปุ่มจริงเท่านั้น — ผู้ใช้ยังลากคัดลอกเองได้ */
    }
  }

  return (
    <div className="setup-scroll">
      <div className="setup">
        <header className="setup-hero lobby-hero">
          <span className="hero-badge">🛸 ห้องเล่นออนไลน์</span>
          <div className="room-code" title="โค้ดห้อง">
            {room.code.split('').map((ch, i) => (
              <span key={i}>{ch}</span>
            ))}
          </div>
          <p>บอกโค้ดนี้ให้เพื่อน หรือส่งลิงก์ด้านล่างไปเลย</p>
        </header>

        <section className="card setup-card">
          <div className="panel-title">ลิงก์ชวนเพื่อน</div>
          <div className="invite">
            <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="ลิงก์ชวนเพื่อน" />
            <button className="btn btn-primary" onClick={copy}>
              {copied ? '✅ คัดลอกแล้ว' : '📋 คัดลอก'}
            </button>
          </div>
          <p className="faint tiny invite-hint">
            เพื่อนเปิดลิงก์แล้วกรอกชื่อ กด "เข้าร่วม" ได้เลย — เข้าได้สูงสุด {MAX_SEATS} คน
            {net.status !== 'open' && <span className="bad-text"> · การเชื่อมต่อสะดุด กำลังต่อใหม่…</span>}
          </p>
        </section>

        <section className="card setup-card">
          <div className="panel-title">
            <span>
              ผู้เล่นในห้อง ({room.seats.length}/{MAX_SEATS})
            </span>
            {isHost && (
              <button className="btn btn-sm" disabled={room.seats.length >= MAX_SEATS} onClick={addBot}>
                + เติมบอท
              </button>
            )}
          </div>

          <div className="seats">
            {room.seats.map((s) => {
              const chr = CHARACTERS.find((c) => c.id === s.characterId) ?? CHARACTERS[0]
              const prof = PROFESSIONS.find((p) => p.id === s.professionId) ?? PROFESSIONS[0]
              const isMe = s.id === net.seatId
              return (
                <div key={s.id} className={`seat${isMe ? ' me' : ''}`} style={{ '--tk': chr.color } as CSSProperties}>
                  <div className="seat-head">
                    <span className="setup-ava" style={{ background: chr.color }}>
                      {chr.emoji}
                    </span>
                    <span className="seat-id">
                      <strong>
                        {s.name}
                        {isMe && <span className="me-tag">คุณ</span>}
                        {s.id === room.hostSeat && <span className="host-tag">เจ้าของห้อง</span>}
                        {s.isBot && <span className="bot-tag">BOT</span>}
                      </strong>
                      <span className="faint tiny">
                        {prof.emoji} {prof.name} · เงินเดือน {money(prof.salary)} · รายจ่าย{' '}
                        {money(professionExpenses(prof))}
                      </span>
                    </span>
                    {!s.isBot && (
                      <span className={`dot ${s.connected ? 'on' : 'off'}`} title={s.connected ? 'ออนไลน์' : 'หลุดการเชื่อมต่อ'} />
                    )}
                    {isHost && s.id !== room.hostSeat && (
                      <button className="btn btn-sm btn-ghost" onClick={() => removeSeat(s.id)} aria-label="เอาออก">
                        ✕
                      </button>
                    )}
                  </div>

                  {isMe && (
                    <>
                      <input
                        value={me?.name ?? ''}
                        maxLength={14}
                        placeholder={chr.name}
                        onChange={(e) => updateSeat({ name: e.target.value })}
                        aria-label="ชื่อของคุณ"
                      />
                      <div className="chr-picker">
                        {CHARACTERS.map((c) => (
                          <button
                            key={c.id}
                            className={`chr${c.id === s.characterId ? ' on' : ''}`}
                            style={{ '--tk': c.color } as CSSProperties}
                            disabled={takenChars.has(c.id)}
                            title={`${c.name} — ${c.tagline}`}
                            onClick={() => updateSeat({ characterId: c.id })}
                          >
                            {c.emoji}
                          </button>
                        ))}
                      </div>
                      <select
                        value={s.professionId}
                        onChange={(e) => updateSeat({ professionId: e.target.value })}
                        aria-label="อาชีพ"
                      >
                        {PROFESSIONS.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.emoji} {p.name} — เงินเดือน {money(p.salary)}
                          </option>
                        ))}
                      </select>
                      <p className="perk">✨ {prof.perk}</p>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        <section className="card setup-card">
          <div className="panel-title">
            <span>ตั้งค่าเกม</span>
            {!isHost && <span className="pill">เจ้าของห้องเป็นคนตั้ง</span>}
          </div>
          <div className="settings">
            <label className="switch">
              <input
                type="checkbox"
                disabled={!isHost}
                checked={room.settings.globalEvents}
                onChange={(e) => updateSettings({ globalEvents: e.target.checked })}
              />
              <span>
                🌍 <b>เหตุการณ์ไซไฟระดับโลก</b>
                <em className="faint tiny">พายุสุริยะ, AI แย่งงาน, ฟองสบู่คริปโตแตก</em>
              </span>
            </label>
            <label className="switch">
              <input
                type="checkbox"
                disabled={!isHost}
                checked={room.settings.personalDebuffs}
                onChange={(e) => updateSettings({ personalDebuffs: e.target.checked })}
              />
              <span>
                🧊 <b>โทษรายตัว</b>
                <em className="faint tiny">แช่แข็งไครโอ, บัญชีถูกแฮก, หลุมดำจิ๋ว</em>
              </span>
            </label>
            <label className="field">
              <span>
                🎯 <b>ความยากของเป้าหมาย</b>
              </span>
              <div className="seg">
                {[
                  { v: 0.75, l: 'สั้น ~45 รอบ' },
                  { v: 1, l: 'มาตรฐาน ~70 รอบ' },
                  { v: 1.5, l: 'มาราธอน' },
                ].map((o) => (
                  <button
                    key={o.v}
                    disabled={!isHost}
                    className={room.settings.freedomMultiplier === o.v ? 'on' : ''}
                    onClick={() => updateSettings({ freedomMultiplier: o.v })}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </label>
          </div>
        </section>

        {net.error && (
          <div className="card net-error">
            <span>⚠️ {net.error}</span>
            <button className="btn btn-sm btn-ghost" onClick={dismissNetError}>
              ปิด
            </button>
          </div>
        )}

        <div className="setup-go">
          {isHost ? (
            <button
              className="btn btn-primary btn-lg"
              disabled={room.seats.length < 2}
              onClick={startOnline}
              title={room.seats.length < 2 ? 'ต้องมีผู้เล่นอย่างน้อย 2 คน (เติมบอทก็ได้)' : ''}
            >
              🚀 เริ่มเกม
            </button>
          ) : (
            <span className="pill waiting">⏳ รอเจ้าของห้องกดเริ่ม… ({humans} คนพร้อมแล้ว)</span>
          )}
          <button className="btn btn-ghost btn-lg" onClick={leaveRoom}>
            ออกจากห้อง
          </button>
        </div>
      </div>
    </div>
  )
}
